import { useDeferredValue, useEffect, useState } from 'react';
import { Alert, App as AntdApp, Button, Form, Modal, Select, Space } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { leadsApi } from '@/core/api/leadsApi';
import { directoryApi } from '@/core/api/directoryApi';
import type { LeadWithEtag } from '@/core/types/lead';
import { useAuthStore } from '@/core/auth/authStore';
import { getErrorMessage } from '@/core/utils/errors';

interface AssignLeadModalProps {
  open: boolean;
  lead: LeadWithEtag | null;
  onClose: () => void;
  onSaved: (result: LeadWithEtag) => void;
}

interface AssignmentValues {
  membershipId: string;
  teamId?: string;
}

export const AssignLeadModal = ({
  open,
  lead,
  onClose,
  onSaved,
}: AssignLeadModalProps): React.ReactElement => {
  const [form] = Form.useForm<AssignmentValues>();
  const { message } = AntdApp.useApp();
  const currentMembershipId = useAuthStore((state) => state.me?.membership.id || '');
  const [memberSearch, setMemberSearch] = useState('');
  const deferredMemberSearch = useDeferredValue(memberSearch);
  const membershipsQuery = useQuery({
    queryKey: ['directory', 'memberships', deferredMemberSearch],
    queryFn: () => directoryApi.memberships({
      search: deferredMemberSearch || undefined,
      status: 'Active',
      page: 1,
      pageSize: 50,
    }),
    enabled: open,
  });
  const selectedMembershipId = Form.useWatch('membershipId', form);
  const selectedMembership = membershipsQuery.data?.items.find(
    (item) => item.membershipId === selectedMembershipId,
  );
  const availableTeams = selectedMembership?.teams || [];

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      membershipId: lead?.lead.ownerMembershipId || currentMembershipId,
      teamId: lead?.lead.ownerTeamId || undefined,
    });
  }, [currentMembershipId, form, lead, open]);

  useEffect(() => {
    if (!open || !selectedMembership) return;
    const selectedTeamId = form.getFieldValue('teamId');
    if (!availableTeams.some((team) => team.id === selectedTeamId)) {
      form.setFieldValue('teamId', selectedMembership.teamId || undefined);
    }
  }, [availableTeams, form, open, selectedMembership]);

  const mutation = useMutation({
    mutationFn: async (values: AssignmentValues) => {
      if (!lead) throw new Error('سرنخ انتخاب نشده است.');
      return leadsApi.assign(
        lead.lead.id,
        { membershipId: values.membershipId, teamId: values.teamId?.trim() || null },
        lead.etag,
      );
    },
    onSuccess: (result) => {
      message.success('سرنخ به مسئول جدید تخصیص یافت.');
      onSaved(result);
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="تخصیص سرنخ"
      footer={null}
      destroyOnHidden
      width={560}
    >
      {mutation.isError ? (
        <Alert
          type="error"
          showIcon
          title="تخصیص ناموفق بود"
          description={getErrorMessage(mutation.error)}
          className="modal-alert"
        />
      ) : null}

      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        <Form.Item
          name="membershipId"
          label="کارشناس مسئول"
          rules={[{ required: true, message: 'کارشناس را انتخاب کنید.' }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={setMemberSearch}
            loading={membershipsQuery.isFetching}
            placeholder="جست‌وجوی نام، ایمیل یا موبایل"
            options={(membershipsQuery.data?.items || []).map((item) => ({
              value: item.membershipId,
              label: `${item.displayName}${item.teamName ? ` — ${item.teamName}` : ''}`,
            }))}
            onChange={(membershipId) => {
              const selected = membershipsQuery.data?.items.find((item) => item.membershipId === membershipId);
              form.setFieldValue('teamId', selected?.teamId || undefined);
            }}
          />
        </Form.Item>

        <Form.Item
          name="teamId"
          label="تیم (اختیاری)"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!selectedMembership || availableTeams.length === 0}
            placeholder={availableTeams.length ? 'انتخاب از تیم‌های این مسئول' : 'این مسئول عضو تیمی نیست'}
            options={availableTeams.map((team) => ({ value: team.id, label: team.name }))}
          />
        </Form.Item>

        <Space className="modal-footer-actions">
          <Button onClick={onClose}>انصراف</Button>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            ثبت تخصیص
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
